import type {
  ActionClickStatus,
  EnrichedAction,
  ResolutionStatus,
} from '../../services/recommendations_contracts'

export interface CompanyActionGroup {
  companyId: number
  companyName: string
  actions: EnrichedAction[]
}

export interface ActionVisibilityGroups {
  activeActions: EnrichedAction[]
  doneActions: EnrichedAction[]
  dismissedActions: EnrichedAction[]
  resolvedActions: EnrichedAction[]
}

export type GetStatusForAction = (
  action: EnrichedAction
) => ActionClickStatus | null

export function isResolvedStatus(
  status: ActionClickStatus | null | undefined
): status is 'done' | 'dismissed' {
  return status === 'done' || status === 'dismissed'
}

/** Check if an action has a resolution_status (from enriched_action dict). */
export function isResolved(action: EnrichedAction): boolean {
  return !!action.resolution_status
}

/** Check if a resolution is a "resolved" type (vs "dismissed"). */
function isResolvedType(rs: ResolutionStatus): boolean {
  return rs === 'auto_resolved' || rs === 'user_resolved'
}

export function partitionActionsByStatus(
  actions: EnrichedAction[],
  getStatusForAction: GetStatusForAction
): ActionVisibilityGroups {
  return actions.reduce<ActionVisibilityGroups>(
    (acc, action) => {
      // Check enriched_action-level resolution first (source of truth)
      if (action.resolution_status) {
        acc.resolvedActions.push(action)
        if (isResolvedType(action.resolution_status)) {
          acc.doneActions.push(action)
        } else {
          acc.dismissedActions.push(action)
        }
        return acc
      }

      // Fall back to click-level status (for actions without resolution_status)
      const status = getStatusForAction(action)

      if (status === 'done') {
        acc.doneActions.push(action)
        acc.resolvedActions.push(action)
        return acc
      }

      if (status === 'dismissed') {
        acc.dismissedActions.push(action)
        acc.resolvedActions.push(action)
        return acc
      }

      acc.activeActions.push(action)
      return acc
    },
    {
      activeActions: [],
      doneActions: [],
      dismissedActions: [],
      resolvedActions: [],
    }
  )
}

/**
 * Group actions by user-facing agent id, using a signal_type → agent_id
 * map derived from the backend AGENT_REGISTRY (delivered via the
 * company-details API). Actions whose signal_type isn't in the map fall
 * into a synthetic "unknown" bucket — surfaces drift loudly rather than
 * silently dropping them.
 *
 * @param actions - The recs to bucket
 * @param signalTypeToAgentId - Map from signal_type to its agent's id
 * @returns Map of agent_id → actions in that agent
 */
export function groupActionsByAgent(
  actions: EnrichedAction[],
  signalTypeToAgentId: Record<string, string>
): Record<string, EnrichedAction[]> {
  const grouped: Record<string, EnrichedAction[]> = {}

  for (const action of actions) {
    const agentId = signalTypeToAgentId[action.signal_type] ?? 'unknown'
    if (!grouped[agentId]) {
      grouped[agentId] = []
    }
    grouped[agentId].push(action)
  }

  return grouped
}

/** Sort actions by urgency_score descending (highest urgency first). */
export function sortByUrgencyScore(
  actions: EnrichedAction[]
): EnrichedAction[] {
  return [...actions].sort((a, b) => b.urgency_score - a.urgency_score)
}

export function groupActionsByCompany(
  actions: EnrichedAction[]
): CompanyActionGroup[] {
  const grouped = actions.reduce(
    (acc, action) => {
      const companyId = action.contact_company_id

      if (!acc[companyId]) {
        acc[companyId] = {
          companyId,
          companyName: action.company_name,
          actions: [],
        }
      }

      acc[companyId].actions.push(action)
      return acc
    },
    {} as Record<number, CompanyActionGroup>
  )

  return Object.values(grouped).sort((a, b) => {
    const aHasEmail = a.actions.some(
      (candidate) => candidate.signal_type === 'unreplied_email'
    )
    const bHasEmail = b.actions.some(
      (candidate) => candidate.signal_type === 'unreplied_email'
    )

    if (aHasEmail && !bHasEmail) return -1
    if (bHasEmail && !aHasEmail) return 1

    return b.actions.length - a.actions.length
  })
}
